// Single source of truth for WO exception-type presentation: display label, avatar
// background color, and the per-type "tail" the card body renders. Mirrors the
// actionType.ts pattern from Ticket 1 (now folded into service.js). Keys are the
// backend WorkOrderExceptionType.value strings
// (crm-common .../enumeration/WorkOrderExceptionType.kt). Colors mirror the legacy
// EXCEPTION_TO_COLOR_MAP so the refreshed cards don't regress, plus the three enum
// values that map never covered (Location, Note, Serial Number). Colors are
// placeholders pending Zac sign-off.
import {
  blue,
  cyan,
  deepOrange,
  deepPurple,
  grey,
  indigo,
  lightBlue,
  lightGreen,
  orange,
  pink,
  purple,
  teal,
  amber,
} from '@mui/material/colors'

export const EXCEPTION_TYPE = {
  COLOR: 'Color',
  CONTAMINATION: 'Contamination',
  DAMAGE: 'Damage',
  DELAY: 'Delay',
  EXTRA_ITEM: 'Extra Item',
  LOCATION: 'Location',
  NO_ACCESS: 'No Access',
  NOTE: 'Note',
  OVERLOADED: 'Overloaded',
  PHOTO: 'Photo',
  SAFETY: 'Safety',
  SERIAL_NUMBER: 'Serial Number',
  SERVICE_REVIEW: 'Service Review',
  SIGN: 'Sign',
  SIZE: 'Size',
  SPECIAL: 'Special',
  WEIGHT: 'Weight',
} as const

export type ExceptionTypeValue = typeof EXCEPTION_TYPE[keyof typeof EXCEPTION_TYPE]

// The card/modal title differs from the raw enum value for these types only
// (the design labels Location as "Set location", Sign as "Signature", Photo as "Camera").
export const EXCEPTION_TYPE_LABEL: Record<string, string> = {
  [EXCEPTION_TYPE.LOCATION]: 'Set location',
  [EXCEPTION_TYPE.SIGN]: 'Signature',
  [EXCEPTION_TYPE.PHOTO]: 'Camera',
}

export const EXCEPTION_TYPE_COLOR: Record<string, string> = {
  [EXCEPTION_TYPE.COLOR]: deepOrange[100],
  [EXCEPTION_TYPE.CONTAMINATION]: indigo[100],
  [EXCEPTION_TYPE.DAMAGE]: orange[100],
  [EXCEPTION_TYPE.DELAY]: purple[100],
  [EXCEPTION_TYPE.EXTRA_ITEM]: amber[100],
  [EXCEPTION_TYPE.LOCATION]: lightGreen[100],
  [EXCEPTION_TYPE.NO_ACCESS]: pink[100],
  [EXCEPTION_TYPE.NOTE]: grey[100],
  [EXCEPTION_TYPE.OVERLOADED]: amber[100],
  [EXCEPTION_TYPE.PHOTO]: teal[100],
  [EXCEPTION_TYPE.SAFETY]: deepPurple[100],
  [EXCEPTION_TYPE.SERIAL_NUMBER]: blue[100],
  [EXCEPTION_TYPE.SERVICE_REVIEW]: blue[100],
  [EXCEPTION_TYPE.SIGN]: grey[100],
  [EXCEPTION_TYPE.SIZE]: cyan[100],
  [EXCEPTION_TYPE.SPECIAL]: grey[100],
  [EXCEPTION_TYPE.WEIGHT]: lightBlue[100],
}

// Per-type "tail" rendered on the right of the card's type-pill row. Only Delay and No
// Access carry a tail: `delay` reads exception.delayTime (timer pill); `subOption` reads
// exception.description (the Blocked / Not out / Locked value). Every other type has no
// tail — its type-specific value lives in the detail modal, not the card. Adding a new
// tail kind = add a renderer under exception-card/per-type/ and map the type here.
export const EXCEPTION_TAIL = {
  DELAY: 'delay',
  SUB_OPTION: 'subOption',
  NONE: 'none',
} as const

export type ExceptionTailKind = typeof EXCEPTION_TAIL[keyof typeof EXCEPTION_TAIL]

const EXCEPTION_TYPE_TAIL: Record<string, ExceptionTailKind> = {
  [EXCEPTION_TYPE.DELAY]: EXCEPTION_TAIL.DELAY,
  [EXCEPTION_TYPE.NO_ACCESS]: EXCEPTION_TAIL.SUB_OPTION,
}

// These types store a per-type value in `description`; all others leave it free, so the web
// mirrors the note into `description` — mobile reads the note from it, not from `noteText`.
const DESCRIPTION_CHANNEL_TYPES: ReadonlySet<string> = new Set([
  EXCEPTION_TYPE.DAMAGE,
  EXCEPTION_TYPE.WEIGHT,
  EXCEPTION_TYPE.SERIAL_NUMBER,
  EXCEPTION_TYPE.SPECIAL,
  EXCEPTION_TYPE.EXTRA_ITEM,
  EXCEPTION_TYPE.NO_ACCESS,
  EXCEPTION_TYPE.DELAY,
])

export const usesDescriptionChannel = (type?: string | null): boolean => typeof type === 'string' && DESCRIPTION_CHANNEL_TYPES.has(type)

export const getExceptionTypeLabel = (type?: string | null): string => (type && EXCEPTION_TYPE_LABEL[type]) || type || ''

export const getExceptionTypeColor = (type?: string | null): string | undefined => (type ? EXCEPTION_TYPE_COLOR[type] : undefined)

// Defaults to NONE: only Delay and No Access render a tail on the card.
export const getExceptionTailKind = (type?: string | null): ExceptionTailKind => (type && EXCEPTION_TYPE_TAIL[type]) || EXCEPTION_TAIL.NONE
